import * as THREE from "three";

/**
 * Procedural placeholder office so the PoC runs with ZERO external assets (and as
 * the Low-tier / splat-failure fallback). Geometry is intentionally simple but
 * lit/shadowed to read as a believable desk scene: desk + monitor + phone, an
 * office chair, a wall chart board, a printer on a side cabinet, a floor plant
 * and a rug. The monitor ([0,0.95,-0.35]) and phone (~[0.62,0.79,-0.05]) anchors
 * line up with the scenario camera waypoints — keep them if you edit this.
 */
export function Office() {
  return (
    <group>
      {/* floor (carpet) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[16, 16]} />
        <meshStandardMaterial color="#5d6470" roughness={1} />
      </mesh>

      {/* rug under the desk. Raised clear of the floor (0) AND the contact-shadow
          plane, with polygonOffset, so the near-coplanar planes don't z-fight and
          shimmer as the camera flies in (the "flickering carpet" bug). */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0.2]} receiveShadow>
        <planeGeometry args={[4.2, 3.0]} />
        <meshStandardMaterial
          color="#3a4250"
          roughness={1}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      </mesh>

      {/* back wall */}
      <mesh position={[0, 2.1, -2.2]} receiveShadow>
        <boxGeometry args={[16, 4.2, 0.2]} />
        <meshStandardMaterial color="#d7dde3" roughness={0.95} />
      </mesh>
      {/* side wall (left) */}
      <mesh position={[-5, 2.1, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <boxGeometry args={[10, 4.2, 0.2]} />
        <meshStandardMaterial color="#cdd4db" roughness={0.95} />
      </mesh>

      {/* desk top */}
      <mesh position={[0, 0.74, -0.2]} castShadow receiveShadow>
        <boxGeometry args={[2.4, 0.05, 1.0]} />
        <meshStandardMaterial color="#2c333b" roughness={0.5} metalness={0.1} />
      </mesh>
      {/* desk edge trim — a thin lighter strip along the front lip so the
          slab reads as a real desk edge instead of a flat single-tone box. */}
      <mesh position={[0, 0.74, 0.301]}>
        <boxGeometry args={[2.4, 0.046, 0.008]} />
        <meshStandardMaterial color="#4a5560" roughness={0.35} metalness={0.25} />
      </mesh>
      {/* desk legs */}
      {[
        [-1.1, 0.37, 0.2],
        [1.1, 0.37, 0.2],
        [-1.1, 0.37, -0.6],
        [1.1, 0.37, -0.6],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.06, 0.74, 0.06]} />
          <meshStandardMaterial color="#1b2127" metalness={0.4} roughness={0.4} />
        </mesh>
      ))}

      {/* monitor (device anchor: desktop) */}
      <group position={[0, 0.95, -0.35]}>
        <mesh castShadow>
          <boxGeometry args={[0.62, 0.38, 0.03]} />
          <meshStandardMaterial color="#0a0d11" roughness={0.4} />
        </mesh>
        {/* thin bezel rim — a slightly lighter edge so the monitor reads as
            a real device with a frame, not a flat black slab. Sits just in
            front of the body's front face (0.015) and well behind the
            screen plane (0.018) so neither coplanar surface z-fights. */}
        <mesh position={[0, 0, 0.0155]}>
          <boxGeometry args={[0.6, 0.36, 0.001]} />
          <meshStandardMaterial color="#20262d" roughness={0.35} metalness={0.35} />
        </mesh>
        {/* screen — OFF (dark); the DOM desktop blends on top once powered on. */}
        <mesh position={[0, 0, 0.018]}>
          <planeGeometry args={[0.58, 0.34]} />
          <meshStandardMaterial color="#0a0d11" roughness={0.5} metalness={0.2} />
        </mesh>
        {/* stand */}
        <mesh position={[0, -0.27, 0]} castShadow>
          <boxGeometry args={[0.08, 0.16, 0.06]} />
          <meshStandardMaterial color="#11161b" />
        </mesh>
        <mesh position={[0, -0.35, 0.02]} castShadow>
          <boxGeometry args={[0.22, 0.02, 0.12]} />
          <meshStandardMaterial color="#11161b" />
        </mesh>
      </group>

      {/* keyboard */}
      <mesh position={[0, 0.77, 0.12]} castShadow>
        <boxGeometry args={[0.45, 0.02, 0.16]} />
        <meshStandardMaterial color="#20272e" roughness={0.6} />
      </mesh>
      {/* mouse */}
      <mesh position={[0.34, 0.77, 0.14]} castShadow>
        <boxGeometry args={[0.06, 0.02, 0.1]} />
        <meshStandardMaterial color="#20272e" roughness={0.6} />
      </mesh>

      {/* desk phone (device anchor: phone) — smartphone lying face-up on the desk */}
      <group position={[0.62, 0.785, -0.02]} rotation={[0, -0.55, 0]}>
        {/* rounded body shell */}
        <mesh castShadow receiveShadow position={[0, 0.009, 0]}>
          <boxGeometry args={[0.082, 0.014, 0.164]} />
          <meshStandardMaterial color="#12161c" roughness={0.28} metalness={0.45} />
        </mesh>
        {/* side bezel rim */}
        <mesh castShadow position={[0, 0.009, 0]}>
          <boxGeometry args={[0.086, 0.011, 0.168]} />
          <meshStandardMaterial color="#2a3038" roughness={0.4} metalness={0.55} />
        </mesh>
        {/* lit screen (face-up) */}
        <mesh position={[0, 0.017, 0.002]} rotation={[-Math.PI / 2, 0, 0]} castShadow>
          <planeGeometry args={[0.07, 0.148]} />
          <meshStandardMaterial
            color="#0c1820"
            roughness={0.2}
            metalness={0.35}
            emissive={new THREE.Color("#2a6a7a")}
            emissiveIntensity={0.55}
          />
        </mesh>
        {/* notch / speaker */}
        <mesh position={[0, 0.018, -0.064]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.032, 0.007]} />
          <meshStandardMaterial color="#05070a" roughness={0.85} />
        </mesh>
        {/* home indicator */}
        <mesh position={[0, 0.018, 0.068]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.028, 0.004]} />
          <meshStandardMaterial color="#3a4550" roughness={0.6} />
        </mesh>
        {/* soft contact shadow */}
        <mesh position={[0, 0.001, 0.01]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[0.11, 0.19]} />
          <meshStandardMaterial color="#000000" transparent opacity={0.28} depthWrite={false} />
        </mesh>
      </group>

      {/* coffee mug */}
      <group position={[-0.55, 0.8, 0.05]}>
        <mesh castShadow>
          <cylinderGeometry args={[0.05, 0.045, 0.09, 16]} />
          <meshStandardMaterial color="#e8eaed" roughness={0.6} />
        </mesh>
      </group>

      <OfficeChair />
      <ChartBoard />
      <PrinterCabinet />
      <FloorPlant />
      <WallTv />
      <Whiteboard />
      <BaseboardTrim />
      <BackgroundDesks />
    </group>
  );
}

/** A wall-mounted whiteboard on the left side wall, away from the TV/chart
 *  board on the back wall — scribbled diagram lines + a few colored sticky
 *  notes and a marker tray, so the room reads as a real working office
 *  rather than empty flat walls. */
function Whiteboard() {
  return (
    <group position={[-4.9, 1.65, 1.5]} rotation={[0, Math.PI / 2, 0]}>
      {/* frame */}
      <mesh castShadow>
        <boxGeometry args={[1.6, 1.0, 0.035]} />
        <meshStandardMaterial color="#c7ccd1" roughness={0.6} metalness={0.05} />
      </mesh>
      {/* board surface */}
      <mesh position={[0, 0, 0.022]}>
        <planeGeometry args={[1.48, 0.88]} />
        <meshStandardMaterial color="#fbfcfd" roughness={0.3} metalness={0.05} />
      </mesh>
      {/* scribbled diagram lines */}
      {[
        [-0.42, 0.24, 0.62, 0.014, "#3671c9"],
        [-0.5, 0.05, 0.42, 0.014, "#3671c9"],
        [-0.38, -0.14, 0.58, 0.014, "#e05a4e"],
        [0.02, 0.24, 0.014, 0.32, "#3671c9"],
      ].map((r, i) => (
        <mesh key={i} position={[r[0] as number, r[1] as number, 0.026]}>
          <planeGeometry args={[r[2] as number, r[3] as number]} />
          <meshStandardMaterial color={r[4] as string} />
        </mesh>
      ))}
      {/* colored sticky notes */}
      {[
        [0.4, 0.18, "#ffe27a"],
        [0.58, 0.02, "#7fe6a0"],
        [0.42, -0.14, "#7fc6ff"],
      ].map((s, i) => (
        <mesh key={i} position={[s[0] as number, s[1] as number, 0.027]} rotation={[0, 0, (i - 1) * 0.08]}>
          <planeGeometry args={[0.13, 0.13]} />
          <meshStandardMaterial color={s[2] as string} />
        </mesh>
      ))}
      {/* marker tray */}
      <mesh position={[0, -0.53, 0.05]} castShadow>
        <boxGeometry args={[1.48, 0.05, 0.09]} />
        <meshStandardMaterial color="#9aa1a8" roughness={0.5} metalness={0.15} />
      </mesh>
      {/* a couple of markers lying in the tray */}
      {[-0.3, 0.15].map((x, i) => (
        <mesh key={i} position={[x, -0.5, 0.07]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.012, 0.012, 0.14, 8]} />
          <meshStandardMaterial color={i === 0 ? "#2b2f36" : "#c23a3a"} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

/** A thin baseboard strip running along the base of the back + side walls —
 *  a small but effective detail that keeps the walls from reading as flat,
 *  featureless "lego" planes meeting the floor with a hard, unfinished edge. */
function BaseboardTrim() {
  return (
    <group>
      <mesh position={[0, 0.06, -2.095]}>
        <boxGeometry args={[16, 0.12, 0.01]} />
        <meshStandardMaterial color="#aab2ba" roughness={0.5} />
      </mesh>
      <mesh position={[-4.995, 0.06, 0]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[10, 0.12, 0.01]} />
        <meshStandardMaterial color="#aab2ba" roughness={0.5} />
      </mesh>
    </group>
  );
}

/** A wall-mounted flat-screen TV on the back wall (the newscast camera frames
 *  this; the DOM bulletin blends over it). Off by default (dark screen). Placed
 *  left of the chart board so the two don't overlap. */
function WallTv() {
  return (
    <group position={[-2.5, 2.15, -2.07]}>
      {/* bezel */}
      <mesh castShadow>
        <boxGeometry args={[1.9, 1.12, 0.06]} />
        <meshStandardMaterial color="#0a0d11" roughness={0.45} metalness={0.2} />
      </mesh>
      {/* dark screen (DOM newscast composites on top) */}
      <mesh position={[0, 0, 0.035]}>
        <planeGeometry args={[1.78, 1.0]} />
        <meshStandardMaterial color="#05070a" roughness={0.5} metalness={0.25} />
      </mesh>
      {/* power LED */}
      <mesh position={[0.82, -0.46, 0.04]}>
        <circleGeometry args={[0.01, 12]} />
        <meshStandardMaterial color="#3ad07a" emissive={new THREE.Color("#1f8f50")} emissiveIntensity={0.7} />
      </mesh>
    </group>
  );
}

/** Rows of simple background desks so the room reads as an open-plan bank floor
 *  ("many desks"). Pure blocks (desk slab + dark monitor + chair stub), set well
 *  behind the hero desk and out of the focus camera so they never distract. */
function BackgroundDesks() {
  const desk = "#252b33";
  const positions: [number, number, number][] = [
    [-3.4, 0, -1.2], [-3.4, 0, 0.2],
    [3.4, 0, -1.2], [3.4, 0, 0.2],
    [-3.4, 0, 1.6], [3.4, 0, 1.6],
  ];
  return (
    <group>
      {positions.map((p, i) => (
        <group key={i} position={p}>
          {/* desk slab */}
          <mesh position={[0, 0.72, 0]} castShadow receiveShadow>
            <boxGeometry args={[1.3, 0.05, 0.7]} />
            <meshStandardMaterial color={desk} roughness={0.6} />
          </mesh>
          {/* legs */}
          {[[-0.55, 0.36, 0.28], [0.55, 0.36, 0.28], [-0.55, 0.36, -0.28], [0.55, 0.36, -0.28]].map(
            (lp, j) => (
              <mesh key={j} position={lp as [number, number, number]} castShadow>
                <boxGeometry args={[0.05, 0.72, 0.05]} />
                <meshStandardMaterial color="#171c22" metalness={0.3} roughness={0.5} />
              </mesh>
            )
          )}
          {/* monitor (off) */}
          <group position={[0, 0.98, -0.12]}>
            <mesh castShadow>
              <boxGeometry args={[0.5, 0.3, 0.025]} />
              <meshStandardMaterial color="#0a0d11" roughness={0.4} />
            </mesh>
            <mesh position={[0, -0.22, 0]}>
              <boxGeometry args={[0.07, 0.14, 0.05]} />
              <meshStandardMaterial color="#11161b" />
            </mesh>
          </group>
          {/* chair stub */}
          <mesh position={[0, 0.45, 0.55]} castShadow>
            <boxGeometry args={[0.42, 0.08, 0.42]} />
            <meshStandardMaterial color="#1c2228" roughness={0.8} />
          </mesh>
          <mesh position={[0, 0.72, 0.74]} rotation={[-0.12, 0, 0]} castShadow>
            <boxGeometry args={[0.42, 0.46, 0.07]} />
            <meshStandardMaterial color="#1c2228" roughness={0.8} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

/** A simple swivel office chair in front of the desk, facing the monitor. */
function OfficeChair() {
  const dark = "#171c22";
  return (
    <group position={[0, 0, 0.7]}>
      {/* seat */}
      <mesh position={[0, 0.48, 0]} castShadow>
        <boxGeometry args={[0.5, 0.09, 0.48]} />
        <meshStandardMaterial color="#22282f" roughness={0.8} />
      </mesh>
      {/* backrest */}
      <mesh position={[0, 0.8, 0.22]} rotation={[-0.12, 0, 0]} castShadow>
        <boxGeometry args={[0.5, 0.56, 0.08]} />
        <meshStandardMaterial color="#22282f" roughness={0.8} />
      </mesh>
      {/* armrests */}
      {[-0.28, 0.28].map((x, i) => (
        <mesh key={i} position={[x, 0.6, 0]} castShadow>
          <boxGeometry args={[0.05, 0.04, 0.34]} />
          <meshStandardMaterial color={dark} roughness={0.5} />
        </mesh>
      ))}
      {/* center pole */}
      <mesh position={[0, 0.27, 0]} castShadow>
        <cylinderGeometry args={[0.035, 0.035, 0.42, 12]} />
        <meshStandardMaterial color={dark} metalness={0.5} roughness={0.4} />
      </mesh>
      {/* 5-star base + casters */}
      {Array.from({ length: 5 }).map((_, i) => {
        const a = (i / 5) * Math.PI * 2;
        const lx = Math.sin(a) * 0.13;
        const lz = Math.cos(a) * 0.13;
        const ex = Math.sin(a) * 0.26;
        const ez = Math.cos(a) * 0.26;
        return (
          <group key={i}>
            <mesh position={[lx, 0.06, lz]} rotation={[0, -a, 0]} castShadow>
              <boxGeometry args={[0.05, 0.04, 0.28]} />
              <meshStandardMaterial color={dark} metalness={0.4} roughness={0.5} />
            </mesh>
            <mesh position={[ex, 0.03, ez]} castShadow>
              <sphereGeometry args={[0.035, 10, 10]} />
              <meshStandardMaterial color="#0c0f13" roughness={0.4} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

/** A framed performance/chart board on the back wall. */
function ChartBoard() {
  const bars = [0.5, 0.78, 0.42, 0.9, 0.64, 0.36];
  return (
    <group position={[2.55, 2.15, -2.08]}>
      {/* frame */}
      <mesh castShadow>
        <boxGeometry args={[1.7, 1.05, 0.05]} />
        <meshStandardMaterial color="#1b2127" roughness={0.5} />
      </mesh>
      {/* board */}
      <mesh position={[0, 0, 0.03]}>
        <planeGeometry args={[1.56, 0.92]} />
        <meshStandardMaterial color="#f4f6f8" roughness={0.9} />
      </mesh>
      {/* title bar */}
      <mesh position={[-0.4, 0.36, 0.032]}>
        <planeGeometry args={[0.66, 0.08]} />
        <meshStandardMaterial color="#4aa89c" />
      </mesh>
      {/* bar chart */}
      {bars.map((h, i) => (
        <mesh key={i} position={[-0.58 + i * 0.2, -0.28 + (h * 0.5) / 2, 0.034]}>
          <planeGeometry args={[0.12, h * 0.5]} />
          <meshStandardMaterial
            color={i % 2 === 0 ? "#2f6f7e" : "#7fb3a8"}
            emissive={new THREE.Color(i % 2 === 0 ? "#244f5a" : "#5c8b82")}
            emissiveIntensity={0.15}
          />
        </mesh>
      ))}
      {/* baseline */}
      <mesh position={[0, -0.3, 0.034]}>
        <planeGeometry args={[1.3, 0.01]} />
        <meshStandardMaterial color="#9aa7b2" />
      </mesh>
    </group>
  );
}

/** A side cabinet to the right of the desk with a printer on top. */
function PrinterCabinet() {
  return (
    <group position={[1.85, 0, -0.5]}>
      {/* cabinet */}
      <mesh position={[0, 0.32, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.7, 0.64, 0.6]} />
        <meshStandardMaterial color="#2a3138" roughness={0.7} />
      </mesh>
      {/* drawer line */}
      <mesh position={[0, 0.32, 0.305]}>
        <planeGeometry args={[0.6, 0.02]} />
        <meshStandardMaterial color="#11161b" />
      </mesh>
      {/* printer body */}
      <mesh position={[0, 0.74, 0]} castShadow>
        <boxGeometry args={[0.56, 0.22, 0.46]} />
        <meshStandardMaterial color="#e6e8ea" roughness={0.6} />
      </mesh>
      {/* printer top tray */}
      <mesh position={[0, 0.86, -0.05]} rotation={[-0.5, 0, 0]} castShadow>
        <boxGeometry args={[0.5, 0.02, 0.16]} />
        <meshStandardMaterial color="#c6cace" roughness={0.6} />
      </mesh>
      {/* output slot */}
      <mesh position={[0, 0.7, 0.231]}>
        <planeGeometry args={[0.46, 0.04]} />
        <meshStandardMaterial color="#10141a" />
      </mesh>
      {/* status light */}
      <mesh position={[0.2, 0.8, 0.231]}>
        <circleGeometry args={[0.012, 12]} />
        <meshStandardMaterial color="#3ad07a" emissive={new THREE.Color("#1f8f50")} emissiveIntensity={0.8} />
      </mesh>
    </group>
  );
}

/** A floor-standing potted plant beside the desk (on the floor, not floating). */
function FloorPlant() {
  return (
    <group position={[-1.85, 0, 0.1]}>
      {/* pot */}
      <mesh position={[0, 0.26, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.13, 0.52, 16]} />
        <meshStandardMaterial color="#9a6a44" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.5, 0]}>
        <cylinderGeometry args={[0.185, 0.18, 0.04, 16]} />
        <meshStandardMaterial color="#7d5536" roughness={0.9} />
      </mesh>
      {/* foliage clusters */}
      <mesh position={[0, 0.78, 0]} castShadow>
        <icosahedronGeometry args={[0.26, 1]} />
        <meshStandardMaterial color="#3f7a4f" roughness={1} flatShading />
      </mesh>
      <mesh position={[0.16, 0.95, 0.05]} castShadow>
        <icosahedronGeometry args={[0.18, 1]} />
        <meshStandardMaterial color="#46885a" roughness={1} flatShading />
      </mesh>
      <mesh position={[-0.14, 1.02, -0.04]} castShadow>
        <icosahedronGeometry args={[0.16, 1]} />
        <meshStandardMaterial color="#356b44" roughness={1} flatShading />
      </mesh>
    </group>
  );
}
